// @TODO: Tests for all of these functions

/**************************************************************************************************************
 * General Functions
 ***************************************************************************************************************/

// Epsilon constants for things like equality tests
export const EPSILON1 = 0.1;
export const EPSILON2 = 0.01;
export const EPSILON3 = 0.001;
export const EPSILON4 = 0.0001;
export const EPSILON5 = 0.00001;
export const EPSILON6 = 0.000001;
export const EPSILON7 = 0.0000001;
export const EPSILON8 = 0.00000001;
export const EPSILON9 = 0.000000001;
export const EPSILON10 = 0.0000000001;
export const EPSILON11 = 0.00000000001;
export const EPSILON12 = 0.000000000001;
export const EPSILON13 = 0.0000000000001;
export const EPSILON14 = 0.00000000000001;
export const EPSILON15 = 0.000000000000001;
export const EPSILON16 = 0.0000000000000001;
export const EPSILON17 = 0.00000000000000001;
export const EPSILON18 = 0.000000000000000001;
export const EPSILON19 = 0.0000000000000000001;
export const EPSILON20 = 0.00000000000000000001;

/**************************************************************************************************************
 * General Functions
 ***************************************************************************************************************/

/** Linearly interpolate between p and q with respect to t.
 *  t is expected to be between 0.0 and 1.0.
 */
// @TODO: Test p and q equal, p > q, t = 0.0, t = 1.0, t < 0.0, t > 1.0, t == 0.5, p = q = t = 0
export const lerp = (p, q, t) => (1.0 - t) * p + t * q;

/** Inverse of the {@link lerp} function. For given values p, q and val, return the t that would yield val if
 *  lerp(p, q, t) were called. lerp(p, q, delerp(p, q, val)) equals val.
 */
export const delerp = (p, q, val) => (val - p) / (q - p);

/** Smoothstep performs smooth Hermite interpolation between 0 and 1 when min < x < max. This is useful in
 *  cases where a threshold function with a smooth transition is desired.
 */
export const smoothstep = (min, max, value) => {
  const x = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return x * x * (3 - 2 * x);
};

/**
 * Determines if two values are equal using an absolute or relative tolerance test. This is useful
 * to avoid problems due to roundoff error when comparing floating-point values directly. The values are
 * first compared using an absolute tolerance test. If that fails, a relative tolerance test is performed.
 * Use this test if you are unsure of the magnitudes of left and right.
 *
 * Examples:
 * var a = AvrMath.equalsEpsilon(0.0, 0.01, AvrMath.EPSILON2); // true
 * var b = AvrMath.equalsEpsilon(0.0, 0.1, AvrMath.EPSILON2);  // false
 * var c = AvrMath.equalsEpsilon(3699175.1634344, 3699175.2, AvrMath.EPSILON7); // true
 * var d = AvrMath.equalsEpsilon(3699175.1634344, 3699175.2, AvrMath.EPSILON9); // false
 */
export const equalsEpsilon = (left, right, relativeEps, absoluteEp) => {
  absoluteEp = absoluteEp !== undefined ? absoluteEp : relativeEps;
  const absDiff = Math.abs(left - right);
  return absDiff <= absoluteEp || absDiff <= relativeEps * Math.max(Math.abs(left), Math.abs(right));
};

/** Get the positive/negative sign of a number, or 0 if their isn't one.
 *  Returns 1 for positive, -1 for neagtive, 0 for everything else.
 */

export const sign = value => {
  if (value > 0) {
    return 1;
  }
  if (value < 0) {
    return -1;
  }

  return 0;
};

/** Clamp a number to a value between min and max, inclusive.
 *  Examples:
 *  var a = clamp(1.1, 0.5, 1.0); // 1.0
 *  var a = clamp(-0.3, 0.0, 0.1); // 0.0
 *  var a = clamp(0.3, 0.0, 0.5); // 0.3
 */

export const clamp = (value, min, max) => Math.max(Math.min(value, max), min);

/** Clamp a number to a value between 0.0 and 1.0, inclusive.
 *  Examples:
 *  var a = saturate(1.1); // 1.0
 *  var a = saturate(-1.2); // 0.0
 */
export const saturate = value => clamp(value, 0.0, 1.0);

/**************************************************************************************************************
 * Cartestian Functions
 ***************************************************************************************************************/

/**
 * Distance between two cartesian points.
 */
export function distance(p0, p1) {
  const x = p0.x - p1.x;
  const y = p0.y - p1.y;
  const z = p0.z - p1.z;
  const m2 = x * x + y * y + z * z;
  return Math.sqrt(m2);
}

/**
 * Computes the distance squared but does not perform the final sqrt function.
 * Faster than {@link distance} and useful for sorting or comparison.
 */
export function distanceSqr(p0, p1) {
  const x = p0.x - p1.x;
  const y = p0.y - p1.y;
  const z = p0.z - p1.z;
  const m2 = x * x + y * y + z * z;
  return m2;
}

/**************************************************************************************************************
 * Gaussian Functions
 ***************************************************************************************************************/

// Returns the output of a probability distribution function with a specified variance and mean: Returns -1 if errors
export function pdf(variance, mean, x) {
  if (variance < 0) {
    return -1;
  }
  const e = Math.exp((x - mean) ** 2 / (-2 * variance));
  const m = Math.sqrt(2 * Math.PI * variance);
  return e / m;
}

// Returns the output of a inverse probability distribution function with a specified variance and mean: Returns -1 if errors
export function invpdf(variance, mean, y) {
  if (variance < 0 || y <= 0) {
    return -1;
  }
  const m = y * Math.sqrt(2 * Math.PI * variance);

  if (Math.log(m) > 0) {
    return -1;
  }
  return Math.sqrt(-2 * variance * Math.log(m)) + mean;
}
